import { useMemo } from 'react';
import {
  useEvaluationComments,
  useQualityConsultantReport,
  useQualityHeatmap,
  useQualitySummary,
  type AllDetailsFiltersState,
  type AllDetailsSortState,
  type AttributeFilter,
  type DashboardQualitySettings,
  type HiddenCriterionRef,
} from '@/entities/dashboards';
import { serializeAttributeFilters } from '@/features/attribute-filters';
import { useDebouncedValue } from '@/shared/hooks';
import { useAllDetailsPages } from './useAllDetailsPages';
import { useDrillDetailsPages, type DrillDetailsQuery } from './useDrillDetailsPages';
import { useAllDetailsQueryParams } from './useAllDetailsQueryParams';
import { getPeriodDateRange } from '../ui/utils';
import { type DashboardFiltersState } from '../ui/QualityFilters';
import { isCriterionHidden } from './hiddenCriteria';
import { type DashboardDrill } from './useDashboardScreenStore';

/**
 * Задержка перед уходом границ балла в запрос списка ячейки. Границы набираются посимвольно, а
 * отбор по ним делает сервер — без паузы каждая цифра стоила бы отдельного обращения.
 */
const DRILL_SCORE_DEBOUNCE_MS = 300;

/** Граница балла для запроса: незаполненное и недобранное поле границы не задаёт. */
const scoreBound = (raw: string): number | null => {
  const value = parseFloat(raw);
  return Number.isFinite(value) ? value : null;
};

/** Входные данные дашборда качества: фильтры, состояние разбора и настройки колонок. */
export type UseDashboardDataParams = {
  filters: DashboardFiltersState;
  activeAttributeFilters: AttributeFilter[];
  detailsSort: AllDetailsSortState | null;
  detailsFilters: AllDetailsFiltersState;
  /** Критерии, скрытые пользователем из отображения (каждый — в разрезе своего заключения). */
  hiddenCriteria: HiddenCriterionRef[];
  /** Выбранная ячейка heatmap для drill-down; `null` — разбор не активен. */
  drill: DashboardDrill;
  /** Нижняя граница фильтра баллов списка ячейки; пустая строка — граница не задана. */
  drillScoreFrom: string;
  /** Верхняя граница фильтра баллов списка ячейки; пустая строка — граница не задана. */
  drillScoreTo: string;
  /** Открытая карточка звонка; `null` — не открыта, комментарии не запрашиваются. */
  selectedCallId: number | null;
  /** ФИО консультанта мини-отчёта; пустая строка — отчёт не запрашивается. */
  reportConsultant: string;
  allDetailsColumns: DashboardQualitySettings['allDetailsColumns'];
};

/**
 * Запросы данных дашборда качества и производные от них величины.
 *
 * Собирает query-параметры из фильтров панели, атрибутных фильтров, сортировки и колоночных
 * фильтров таблицы «Все детали», выполняет запросы агрегированной сводки, тепловой карты,
 * мини-отчёта консультанта, таблицы деталей и списка звонков выбранной ячейки (drill-down), и
 * возвращает готовые к отображению данные вместе с максимумами для нормировки графиков и
 * средними по видимым (не скрытым) критериям.
 */
export const useDashboardData = ({
  filters,
  activeAttributeFilters,
  detailsSort,
  detailsFilters,
  hiddenCriteria,
  drill,
  drillScoreFrom,
  drillScoreTo,
  selectedCallId,
  reportConsultant,
  allDetailsColumns,
}: UseDashboardDataParams) => {
  // Сериализованная строка для query-параметра; null — параметр не передаётся
  const attributeFiltersParam = useMemo(
    () => serializeAttributeFilters(activeAttributeFilters),
    [activeAttributeFilters],
  );

  // Ссылка стабильна: этими же фильтрами переход собирает состав показателя, и пересборка
  // объекта на каждый рендер заново пересобирала бы обработчики всех карточек.
  const summaryParams = useMemo(
    () => ({
      mode: filters.heatmapMode,
      'consultant-name': filters.consultant || null,
      'attribute-filters': attributeFiltersParam,
      'date-from': filters.dateFrom || null,
      'date-to': filters.dateTo || null,
    }),
    [filters.heatmapMode, filters.consultant, filters.dateFrom, filters.dateTo, attributeFiltersParam],
  );

  const {
    data: summaryData,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
  } = useQualitySummary(summaryParams);

  const { data: heatmapData, isLoading: heatmapLoading } = useQualityHeatmap(summaryParams);

  const { data: reportData } = useQualityConsultantReport(
    reportConsultant
      ? {
          'consultant-name': reportConsultant,
          'attribute-filters': attributeFiltersParam,
          'date-from': filters.dateFrom || null,
          'date-to': filters.dateTo || null,
        }
      : null,
  );

  // Сортировка, колоночные фильтры и состав колонок таблицы «Все детали» — в параметры её запроса.
  const { sortParam, blockRatingParam, columnsParam } = useAllDetailsQueryParams({
    detailsSort,
    detailsFilters,
    allDetailsColumns,
  });

  // Условия отбора таблицы: сами строки приходят окнами по мере прокрутки, поэтому здесь только
  // отбор — без границ окна.
  const detailsQuery = useMemo(
    () => ({
      'attribute-filters': attributeFiltersParam,
      'date-from': filters.dateFrom || null,
      'date-to': filters.dateTo || null,
      sort: sortParam,
      'filter-external-id': detailsFilters.externalId || null,
      'filter-consultant-name': detailsFilters.consultantName || null,
      'filter-client-name': detailsFilters.clientName || null,
      'filter-total-rating-from': detailsFilters.totalRatingFrom ?? null,
      'filter-total-rating-to': detailsFilters.totalRatingTo ?? null,
      'filter-block-rating': blockRatingParam,
      columns: columnsParam,
    }),
    [
      attributeFiltersParam,
      filters.dateFrom,
      filters.dateTo,
      sortParam,
      detailsFilters.externalId,
      detailsFilters.consultantName,
      detailsFilters.clientName,
      detailsFilters.totalRatingFrom,
      detailsFilters.totalRatingTo,
      blockRatingParam,
      columnsParam,
    ],
  );

  const allDetails = useAllDetailsPages(detailsQuery);

  // ===== Список звонков выбранной ячейки heatmap (drill-down) =====
  // Ячейку целиком задаёт запрос — её консультант, её период и границы балла карточки,
  // — поэтому строки приходят готовыми, а итоги считаны по всей ячейке. Строить список из
  // общей выборки деталей нельзя: она приходит своими окнами, и у ячеек, чьи оценки в
  // показанное окно не попали, список оказывался пуст при наличии балла. Режимы по месяцам и
  // по неделям — одинаково.
  const drillRange = useMemo(() => {
    if (!drill) return null;
    const periodRange = getPeriodDateRange(drill.period, filters.heatmapMode);
    if (!periodRange) return null;
    // Пересечение периода ячейки с периодом панели: ячейка построена только по
    // оценкам в границах панели — тем же ограничиваем и её список.
    const from = filters.dateFrom && filters.dateFrom > periodRange.from ? filters.dateFrom : periodRange.from;
    const to = filters.dateTo && filters.dateTo < periodRange.to ? filters.dateTo : periodRange.to;
    return { from, to };
  }, [drill, filters.heatmapMode, filters.dateFrom, filters.dateTo]);

  // Границы балла карточки уходят в тот же запрос: итоги списка описывают всю ячейку, а
  // отобранная её часть — так же целиком, поэтому отбор по баллу делает сервер, а не клиент.
  const debouncedScoreFrom = useDebouncedValue(drillScoreFrom, DRILL_SCORE_DEBOUNCE_MS);
  const debouncedScoreTo = useDebouncedValue(drillScoreTo, DRILL_SCORE_DEBOUNCE_MS);

  const drillQuery = useMemo<DrillDetailsQuery | null>(() => {
    if (!drill || !drillRange) return null;
    return {
      'attribute-filters': attributeFiltersParam,
      'date-from': drillRange.from,
      'date-to': drillRange.to,
      // Точное ФИО, которым ячейку называет тепловая карта: подстрочный отбор притянул бы
      // однофамильцев, и список разошёлся бы с ячейкой, которую он раскрывает.
      'consultant-name': drill.consultant,
      'filter-total-rating-from': scoreBound(debouncedScoreFrom),
      'filter-total-rating-to': scoreBound(debouncedScoreTo),
    };
  }, [drill, drillRange, attributeFiltersParam, debouncedScoreFrom, debouncedScoreTo]);

  // Строки списка приходят окнами по мере прокрутки, а итоги ячейки — тем же ответом: у ячейки,
  // где оценок больше окна, средний балл иначе расходился бы с самой ячейкой.
  const drillPages = useDrillDetailsPages(drillQuery);

  // ===== Открытая оценка =====
  // Комментарии оценки список не несёт — их запрашивает открытая карточка звонка.
  const { data: commentsData } = useEvaluationComments(selectedCallId);
  const callComments = useMemo(
    () => new Map(commentsData ? commentsData.blocks.map((block) => [block.blockId, block.comment]) : []),
    [commentsData],
  );

  const kpis = summaryData?.kpis ?? null;
  const blockNames = summaryData?.blockNames ?? [];
  const consultantNames = summaryData?.consultantNames ?? [];

  // Средние баллы по критериям без скрытых пользователем (настройка отображения) и без критериев
  // с нулевым потолком шкалы (maxScore === 0). Скрытие адресует критерий вместе с его
  // заключением-владельцем, поэтому одноимённый критерий другого заключения остаётся видимым.
  // Скрытый критерий уходит из графика «Критерии», из
  // выделения крайних критериев в сводке (слабейший/сильнейший) и из KPI «Проблемный критерий»
  // (слабейший видимый критерий). Критерий с maxScore === 0 набрать нельзя — его среднее всегда 0,
  // и без исключения он ложно всплывал бы слабейшим/проблемным. Прочие KPI (средний балл, кол-во,
  // консультанты, процент низких) от этого не зависят — их считает бэк по всем критериям.
  const visibleCriteriaAverages = useMemo(
    () =>
      (summaryData?.criteriaAverages ?? []).filter(
        (b) => !isCriterionHidden(hiddenCriteria, b.expertReportId, b.name) && b.maxScore !== 0,
      ),
    [summaryData?.criteriaAverages, hiddenCriteria],
  );

  // Максимумы для нормировки столбчатых графиков. Общий максимум карточки нужен только там, где
  // бары сравниваются между собой по абсолютной величине (распределение, динамика). График
  // критериев нормируется долей балла от максимума своей шкалы у каждого критерия
  // (см. CriteriaAveragesCard), рейтинг консультантов — долей балла консультанта от его
  // собственного максимума, поэтому общий максимум им обоим не нужен.
  const distributionMax = useMemo(
    () => Math.max(...(summaryData?.distribution ?? []).map((b) => b.count), 1),
    [summaryData?.distribution],
  );
  const trendMax = useMemo(() => Math.max(...(summaryData?.trend ?? []).map((b) => b.avg), 1), [summaryData?.trend]);

  return {
    summaryParams,
    summaryData,
    summaryLoading,
    summaryFetching,
    heatmapData,
    heatmapLoading,
    reportData,
    allDetails,
    kpis,
    blockNames,
    consultantNames,
    visibleCriteriaAverages,
    distributionMax,
    trendMax,
    drillPages,
    callComments,
  };
};
